import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { CheckCircle2, XCircle, Clock } from "lucide-react";
import { trackPurchase } from "../lib/metaPixel";
import Seo from "../components/Seo";
import { trackTTPurchase } from "../lib/tiktokPixel";
import { ga4Purchase } from "../lib/ga4";
import TX from "../components/TX";

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const paypalOrderId = params.get("paypal");
  const [state, setState] = useState({ kind: "polling", attempt: 0 });
  const [details, setDetails] = useState(null);
  const purchaseFiredRef = useRef(false);

  useEffect(() => {
    // PayPal flow — capture already happened client-side; just confirm + fire Purchase
    if (paypalOrderId && !sessionId) {
      setState({ kind: "paid" });
      if (!purchaseFiredRef.current) {
        purchaseFiredRef.current = true;
        const purchasePayload = {
          orderId: paypalOrderId,
          gemName: "PayPal acquisition",
          value: 0,
          currency: "USD",
        };
        trackPurchase(purchasePayload);
        trackTTPurchase(purchasePayload);
        ga4Purchase(purchasePayload);
      }
      return;
    }
    if (!sessionId) { setState({ kind: "error", message: "Missing session id" }); return; }
    let cancelled = false;
    let attempts = 0;
    const max = 8;

    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        setDetails(data);
        if (data.payment_status === "paid") {
          setState({ kind: "paid" });
          if (!purchaseFiredRef.current) {
            purchaseFiredRef.current = true;
            const purchasePayload = {
              gemId: data.metadata?.gemstone_id,
              gemName: data.metadata?.gemstone_name,
              value: (data.amount_total || 0) / 100,
              currency: (data.currency || "USD").toUpperCase(),
              email: data.metadata?.user_email,
              orderId: sessionId,
            };
            trackPurchase(purchasePayload);
            trackTTPurchase(purchasePayload);
            ga4Purchase(purchasePayload);
          }
          return;
        }
        if (data.status === "expired") { setState({ kind: "expired" }); return; }
        attempts += 1;
        setState({ kind: "polling", attempt: attempts });
        if (attempts >= max) { setState({ kind: "timeout" }); return; }
        setTimeout(poll, 2000);
      } catch (e) {
        if (!cancelled) setState({ kind: "error", message: e.response?.data?.detail || "Could not verify payment" });
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div data-testid="checkout-success-page" className="pt-32 max-w-2xl mx-auto px-6 pb-32 text-center">
      <Seo title="Order Confirmed" description="Your OPIKJEWELS order has been received." path="/checkout/success" noIndex />
      {state.kind === "polling" && (
        <>
          <Clock className="mx-auto text-gold mb-6 animate-pulse" size={48} />
          <p className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-5"><TX>Verifying Payment</TX></p>
          <h1 className="font-heading font-light text-4xl mb-4"><TX>Confirming with Stripe…</TX></h1>
          <p className="text-white/60"><TX>Please do not close this window.</TX></p>
        </>
      )}
      {state.kind === "paid" && (
        <>
          <CheckCircle2 className="mx-auto text-gold mb-6" size={48} />
          <p className="font-accent text-[11px] tracking-[0.28em] uppercase text-gold mb-5"><TX>Acquisition Confirmed</TX></p>
          <h1 className="font-heading font-light text-5xl mb-6"><TX>Welcome, custodian.</TX></h1>
          <p className="text-white/60 leading-relaxed mb-8"><TX>Your acquisition has been recorded. A senior consultant will reach out within 24 hours regarding secure shipping, certification handover, and lifetime aftercare.</TX></p>
          {details && (
            <div className="bg-onyx border border-gold/30 p-8 text-left mb-10">
              <div className="font-heading text-2xl mb-2">{details.metadata?.gemstone_name}</div>
              <div className="text-white/50 text-sm font-accent tracking-[0.18em] uppercase"><TX>Reference</TX> · {sessionId.slice(0, 16)}…</div>
              <div className="mt-4 text-ivory font-heading text-3xl">
                {(details.amount_total / 100).toLocaleString()} {(details.currency || "USD").toUpperCase()}
              </div>
            </div>
          )}
          <Link to="/" data-testid="success-home-link" className="inline-block bg-gold text-black font-accent uppercase tracking-[0.28em] text-xs px-9 py-4 hover:bg-ivory transition-colors duration-500"><TX>Return Home</TX></Link>
        </>
      )}
      {state.kind === "expired" && (
        <>
          <XCircle className="mx-auto text-red-500 mb-6" size={48} />
          <h1 className="font-heading text-4xl mb-4"><TX>Payment Session Expired</TX></h1>
          <Link to="/collection" className="inline-block border border-white/30 text-white font-accent uppercase tracking-[0.28em] text-xs px-9 py-4 hover:border-gold hover:text-gold"><TX>Back to Collection</TX></Link>
        </>
      )}
      {state.kind === "timeout" && (
        <>
          <Clock className="mx-auto text-yellow-500 mb-6" size={48} />
          <h1 className="font-heading text-4xl mb-4"><TX>Verification Timeout</TX></h1>
          <p className="text-white/60 mb-8"><TX>Please check your email for confirmation.</TX></p>
          <Link to="/" className="inline-block border border-white/30 text-white font-accent uppercase tracking-[0.28em] text-xs px-9 py-4"><TX>Return Home</TX></Link>
        </>
      )}
      {state.kind === "error" && (
        <>
          <XCircle className="mx-auto text-red-500 mb-6" size={48} />
          <h1 className="font-heading text-4xl mb-4"><TX>Could Not Verify</TX></h1>
          <p className="text-white/60 mb-8">{state.message}</p>
          <Link to="/" className="inline-block border border-white/30 text-white font-accent uppercase tracking-[0.28em] text-xs px-9 py-4"><TX>Return Home</TX></Link>
        </>
      )}
    </div>
  );
};

export default CheckoutSuccess;
